// Counterpart of webapp/src/mutator.ts: every user-facing mutation goes here so
// the store update and the (mock) server round-trip stay in one place.
//
// NOT ported: upstream's undo/redo stack (src/undomanager.ts) and the
// WebSocket broadcast that follows each mutation. See README "未复刻".

import {Block, BlockTypes, createBlock, createPatchesFromBlocks} from '../types/block'
import {Board, IPropertyOption, IPropertyTemplate, MemberRole} from '../types/board'
import {Card, createCard as hydrateCard} from '../types/card'
import {BoardView, IViewType, createBoardView, viewTitleOf} from '../types/boardView'
import {FilterClause, FilterGroup} from '../types/filterGroup'
import {Constants} from '../common/constants'
import {Utils, IDType} from '../common/utils'
import client from '../api/client'
import {useBoards} from '../store/boards'
import {useCards} from '../store/cards'
import {useViews} from '../store/views'
import {useContents} from '../store/contents'
import {useComments, CommentBlock} from '../store/comments'
import {useUsers} from '../store/users'

function meId(): string {
    return useUsers.getState().me?.id || ''
}

/** Writes only the delta, using the same patch shape the server expects. */
function persistBlock(boardId: string, prev: Block, next: Block): Promise<boolean> {
    const [patch] = createPatchesFromBlocks(next, prev)
    return client.patchBlock(boardId, next.id, patch)
}

// ------------------------------------------------------------- cards ------

export async function createCard(board: Board, view: BoardView | undefined, options: {title?: string, icon?: string, properties?: Record<string, string | string[]>, isTemplate?: boolean} = {}): Promise<Card> {
    const me = meId()
    const card = hydrateCard({
        id: Utils.createBlockId('card'),
        boardId: board.id,
        parentId: board.id,
        type: 'card',
        title: options.title || '未命名',
        createdBy: me,
        modifiedBy: me,
    })
    card.fields.icon = options.icon || ''
    card.fields.properties = {...(options.properties || {}), ...propertiesFromFilter(view)}
    if (options.isTemplate) {
        card.fields.isTemplate = true
    }

    useCards.getState().upsertCard(card)
    const created = await client.insertBlock(card)
    useCards.getState().upsertCard(hydrateCard(created))

    if (view) {
        const order = view.fields.cardOrder.slice()
        order.unshift(card.id)
        await patchViewFields(board.id, view, {cardOrder: order})
    }
    return card
}

/** New cards inherit an "equals" filter value, like upstream's add-card menu. */
function propertiesFromFilter(view?: BoardView): Record<string, string> {
    const out: Record<string, string> = {}
    if (!view) {
        return out
    }
    view.fields.filter.filters.forEach((filter) => {
        if ('condition' in filter && (filter.condition === 'is' || filter.condition === 'includes') && filter.values.length === 1) {
            out[filter.propertyId] = filter.values[0]
        }
    })
    return out
}

export async function changeCardTitle(boardId: string, card: Card, title: string, description = '修改卡片标题'): Promise<void> {
    void description
    const next: Card = {...card, title, updateAt: Date.now()}
    useCards.getState().upsertCard(next)
    await persistBlock(boardId, card, next)
}

export async function changeCardIcon(boardId: string, card: Card, icon: string): Promise<void> {
    const next: Card = {...card, fields: {...card.fields, icon}}
    useCards.getState().upsertCard(next)
    await persistBlock(boardId, card, next)
}

export async function changeCardFieldValue(boardId: string, card: Card, propertyId: string, value: string | string[] | undefined): Promise<void> {
    const properties = {...card.fields.properties}
    if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete properties[propertyId]
    } else {
        properties[propertyId] = value
    }
    const next: Card = {...card, fields: {...card.fields, properties}, updateAt: Date.now()}
    useCards.getState().upsertCard(next)
    await persistBlock(boardId, card, next)
}

export async function archiveCard(boardId: string, card: Card): Promise<void> {
    const deleteAt = Date.now()
    const next: Card = {...card, deleteAt}
    useCards.getState().upsertCard(next)
    await client.deleteBlock(boardId, card.id)
}

export async function restoreCard(boardId: string, card: Card): Promise<void> {
    const next: Card = {...card, deleteAt: 0}
    useCards.getState().upsertCard(next)
    await client.undeleteBlock(boardId, card.id)
}

// ------------------------------------------------------------- views ------

export async function patchViewFields(boardId: string, view: BoardView, fields: Partial<BoardView['fields']>): Promise<BoardView> {
    const next: BoardView = {...view, fields: {...view.fields, ...fields}, updateAt: Date.now()}
    useViews.getState().upsertView(next)
    await client.patchBlock(boardId, view.id, {updatedFields: next.fields})
    return next
}

export const changeViewGroupBy = (boardId: string, view: BoardView, groupById: string | undefined) =>
    patchViewFields(boardId, view, {groupById})

export const changeViewSortBy = (boardId: string, view: BoardView, propertyId: string, reversed: boolean) =>
    patchViewFields(boardId, view, {sortOptions: [{propertyId, reversed}]})

export const changeViewShowBy = (boardId: string, view: BoardView, dateDisplayPropertyId: string | undefined) =>
    patchViewFields(boardId, view, {dateDisplayPropertyId})

export const changeViewCollapsedOptions = (boardId: string, view: BoardView, collapsedOptionIds: string[]) =>
    patchViewFields(boardId, view, {collapsedOptionIds})

export const changeViewVisibleOptionIds = (boardId: string, view: BoardView, visibleOptionIds: string[]) =>
    patchViewFields(boardId, view, {visibleOptionIds})

export const changeViewHiddenOptionIds = (boardId: string, view: BoardView, hiddenOptionIds: string[]) =>
    patchViewFields(boardId, view, {hiddenOptionIds})

export const changeViewColumnWidth = (boardId: string, view: BoardView, propertyId: string, width: number) =>
    patchViewFields(boardId, view, {columnWidths: {...view.fields.columnWidths, [propertyId]: width}})

export async function changeViewFilter(boardId: string, view: BoardView, filter: FilterGroup): Promise<void> {
    await patchViewFields(boardId, view, {filter})
}

export async function addViewFilter(boardId: string, view: BoardView, clause: FilterClause): Promise<void> {
    const filter: FilterGroup = {
        ...view.fields.filter,
        filters: [...view.fields.filter.filters, clause],
    }
    await changeViewFilter(boardId, view, filter)
}

export async function removeViewFilter(boardId: string, view: BoardView, index: number): Promise<void> {
    const filters = view.fields.filter.filters.slice()
    filters.splice(index, 1)
    await changeViewFilter(boardId, view, {...view.fields.filter, filters})
}

export async function changeViewVisibleProperties(boardId: string, view: BoardView, visiblePropertyIds: string[]): Promise<void> {
    await patchViewFields(boardId, view, {visiblePropertyIds})
}

export async function createView(board: Board, viewType: IViewType): Promise<BoardView> {
    const view = createBoardView({
        id: Utils.createBlockId('view'),
        boardId: board.id,
        parentId: board.id,
        type: 'view',
        title: viewTitleOf(viewType),
        createdBy: meId(),
    })
    view.fields.viewType = viewType
    view.fields.visiblePropertyIds = board.cardProperties.map((p) => p.id)
    view.fields.groupById = viewType === 'table' || viewType === 'calendar' ? undefined : board.cardProperties.find((p) => p.type === 'select')?.id
    if (viewType === 'calendar') {
        view.fields.dateDisplayPropertyId = board.cardProperties.find((p) => p.type === 'date')?.id
    }

    useViews.getState().upsertView(view)
    const created = await client.insertBlock(view)
    const hydrated = createBoardView(created)
    useViews.getState().upsertView(hydrated)
    return hydrated
}

export async function renameView(boardId: string, view: BoardView, title: string): Promise<void> {
    const next: BoardView = {...view, title}
    useViews.getState().upsertView(next)
    await client.patchBlock(boardId, view.id, {title})
}

export async function archiveView(boardId: string, view: BoardView): Promise<void> {
    useViews.getState().removeView(view.id)
    await client.deleteBlock(boardId, view.id)
}

/** Reorder inside a column, or move a card across columns. */
export async function moveCard(boardId: string, view: BoardView, draggedCard: Card, targetGroupOptionId: string | undefined, targetCard: Card | undefined, toTopOfGroup = false): Promise<void> {
    const groupPropertyId = view.fields.groupById
    const awaits: Array<Promise<unknown>> = []

    if (groupPropertyId && targetGroupOptionId !== undefined) {
        const currentValue = draggedCard.fields.properties[groupPropertyId] || ''
        if (currentValue !== targetGroupOptionId) {
            awaits.push(changeCardFieldValue(boardId, draggedCard, groupPropertyId, targetGroupOptionId || undefined))
        }
    }

    const order = view.fields.cardOrder.slice()
    const fromIndex = order.indexOf(draggedCard.id)
    if (fromIndex >= 0) {
        order.splice(fromIndex, 1)
    }
    let toIndex = targetCard ? order.indexOf(targetCard.id) : -1
    if (toIndex < 0) {
        toIndex = toTopOfGroup ? 0 : order.length
    } else if (toTopOfGroup) {
        // keep as is: inserting above the target
    } else {
        toIndex += 1
    }
    order.splice(toIndex, 0, draggedCard.id)
    awaits.push(patchViewFields(boardId, view, {cardOrder: order}))
    await Promise.all(awaits)
}

// ------------------------------------------------------------- boards -----

export async function patchBoard(boardId: string, patch: Partial<Board>): Promise<void> {
    const prev = useBoards.getState().boards[boardId]
    if (!prev) {
        return
    }
    const next = {...prev, ...patch, updateAt: Date.now()}
    useBoards.getState().upsertBoard(next)
    await client.patchBoard(boardId, patch as any)
}

export async function changeBoardTitle(boardId: string, title: string): Promise<void> {
    await patchBoard(boardId, {title})
}

export async function changeBoardDescription(boardId: string, description: string, showDescription: boolean): Promise<void> {
    await patchBoard(boardId, {description, showDescription})
}

export async function changeBoardIcon(boardId: string, icon: string): Promise<void> {
    await patchBoard(boardId, {icon})
}

export async function deleteBoard(boardId: string): Promise<void> {
    useBoards.getState().removeBoard(boardId)
    await client.deleteBoard(boardId)
}

export async function createBoardWithViews(teamId: string, init: {title: string, description?: string, icon?: string, cardProperties?: IPropertyTemplate[], fromTemplate?: Board}): Promise<Board> {
    const template = init.fromTemplate
    const board: Board = {
        id: Utils.createGuid(IDType.Board),
        teamId,
        channelId: Constants.noChannelID,
        createdBy: meId(),
        modifiedBy: meId(),
        type: template?.type || 'P',
        minimumRole: template?.minimumRole || MemberRole.None,
        title: init.title || '未命名',
        description: init.description ?? template?.description ?? '',
        icon: init.icon ?? template?.icon ?? '',
        showDescription: false,
        isTemplate: false,
        templateVersion: 1,
        properties: {},
        cardProperties: Utils.deepClone(init.cardProperties || template?.cardProperties || []),
        createAt: Date.now(),
        updateAt: Date.now(),
        deleteAt: 0,
    }

    const created = await useBoards.getState().createBoard(board)

    const viewTypes: IViewType[] = ['board', 'table', 'gallery', 'calendar']
    await Promise.all(viewTypes.map(async (viewType, i) => {
        const view = createBoardView({
            id: Utils.createBlockId('view'),
            boardId: created.id,
            parentId: created.id,
            type: 'view',
            title: viewTitleOf(viewType),
            createAt: Date.now() + i,
        })
        view.fields.viewType = viewType
        view.fields.visiblePropertyIds = created.cardProperties.map((p) => p.id)
        view.fields.groupById = viewType === 'table' || viewType === 'calendar' ? undefined : created.cardProperties.find((p) => p.type === 'select')?.id
        if (viewType === 'calendar') {
            view.fields.dateDisplayPropertyId = created.cardProperties.find((p) => p.type === 'date')?.id
        }
        useViews.getState().upsertView(view)
        await client.insertBlock(view)
    }))

    return created
}

// ---------------------------------------------------- card properties -----

export async function addCardProperty(board: Board, template: IPropertyTemplate): Promise<void> {
    const next = {...board, cardProperties: [...board.cardProperties, template]}
    await updateBoardProperties(board, next)
}

export async function updateCardProperty(board: Board, template: IPropertyTemplate): Promise<void> {
    const next = {...board, cardProperties: board.cardProperties.map((p) => (p.id === template.id ? template : p))}
    await updateBoardProperties(board, next)
}

export async function deleteCardProperty(board: Board, template: IPropertyTemplate): Promise<void> {
    const next = {...board, cardProperties: board.cardProperties.filter((p) => p.id !== template.id)}
    await updateBoardProperties(board, next)
}

async function updateBoardProperties(prev: Board, next: Board): Promise<void> {
    useBoards.getState().upsertBoard({...next, updateAt: Date.now()})
    await client.patchBoard(prev.id, {
        updatedCardProperties: next.cardProperties,
        deletedCardProperties: prev.cardProperties.filter((p) => !next.cardProperties.find((np) => np.id === p.id)).map((p) => p.id),
    })
}

export async function insertPropertyOption(board: Board, template: IPropertyTemplate, option: IPropertyOption): Promise<void> {
    await updateCardProperty(board, {...template, options: [...template.options, option]})
}

export async function changePropertyOption(board: Board, template: IPropertyTemplate, option: IPropertyOption): Promise<void> {
    await updateCardProperty(board, {...template, options: template.options.map((o) => (o.id === option.id ? option : o))})
}

export async function deletePropertyOption(board: Board, template: IPropertyTemplate, option: IPropertyOption): Promise<void> {
    await updateCardProperty(board, {...template, options: template.options.filter((o) => o.id !== option.id)})

    // drop the value from every card that still points at it
    const cards = Object.values(useCards.getState().cards).filter((c) => c.boardId === board.id)
    await Promise.all(cards.
        filter((c) => c.fields.properties[template.id] === option.id).
        map((c) => changeCardFieldValue(board.id, c, template.id, undefined)))
}

// ------------------------------------------------------ content blocks ----

export async function createContentBlock(boardId: string, card: Card, block: Block, targetBlockId?: string, position: 'top'|'bottom'|'above'|'below' = 'bottom'): Promise<Block> {
    const newBlock = createBlock({...block, boardId, parentId: card.id, createdBy: meId()})

    const order = card.fields.contentOrder.slice()
    let index = targetBlockId ? order.findIndex((id) => id === targetBlockId || (Array.isArray(id) && id.includes(targetBlockId))) : -1
    if (index < 0) {
        index = position === 'top' ? 0 : order.length
    } else if (position === 'below') {
        index += 1
    }
    order.splice(index, 0, newBlock.id)

    const nextCard: Card = {...card, fields: {...card.fields, contentOrder: order}}
    useContents.getState().upsertBlock(newBlock)
    useCards.getState().upsertCard(nextCard)
    await client.insertBlock(newBlock)
    await client.patchBlock(boardId, card.id, {updatedFields: {contentOrder: order}})
    return newBlock
}

export async function changeBlockTitle(boardId: string, block: Block, title: string): Promise<void> {
    const next: Block = {...block, title, updateAt: Date.now()}
    useContents.getState().upsertBlock(next)
    await persistBlock(boardId, block, next)
}

/** Checkbox blocks carry their state in `fields.value` (verified upstream). */
export async function changeCheckboxValue(boardId: string, block: Block, checked: boolean): Promise<void> {
    const next: Block = {...block, fields: {...block.fields, value: checked}, updateAt: Date.now()}
    useContents.getState().upsertBlock(next)
    await client.patchBlock(boardId, block.id, {updatedFields: {value: checked}})
}

export async function deleteContentBlock(boardId: string, card: Card, block: Block): Promise<void> {
    const order = card.fields.contentOrder.filter((id) => id !== block.id && !(Array.isArray(id) && id.includes(block.id)))
    const nextCard: Card = {...card, fields: {...card.fields, contentOrder: order}}

    useContents.getState().archiveBlock(block.id, Date.now())
    useCards.getState().upsertCard(nextCard)
    await client.deleteBlock(boardId, block.id)
    await client.patchBlock(boardId, card.id, {updatedFields: {contentOrder: order}})
}

export const contentBlockLabels: Record<BlockTypes, string> = {
    text: '文本',
    h1: '一级标题',
    h2: '二级标题',
    h3: '三级标题',
    checkbox: '待办事项',
    divider: '分割线',
    'list-item': '无序列表',
    image: '图片',
    attachment: '附件',
    quote: '引用',
    video: '视频',
    board: '看板',
    view: '视图',
    card: '卡片',
    comment: '评论',
    unknown: '未知',
}

// ------------------------------------------------------------ comments ----

export async function insertComment(boardId: string, card: Card, text: string): Promise<CommentBlock> {
    const now = Date.now()
    const me = meId()
    const comment: CommentBlock = {
        id: Utils.createBlockId('comment'),
        boardId,
        parentId: card.id,
        createdBy: me,
        modifiedBy: me,
        schema: 1,
        type: 'comment',
        title: text,
        fields: {},
        createAt: now,
        updateAt: now,
        deleteAt: 0,
    }
    useComments.getState().upsertComment(comment)
    const created = await client.insertBlock(comment)
    useComments.getState().upsertComment({...comment, ...created, type: 'comment'} as CommentBlock)
    return comment
}

export async function updateComment(boardId: string, comment: CommentBlock, text: string): Promise<void> {
    const next: CommentBlock = {...comment, title: text, updateAt: Date.now()}
    useComments.getState().upsertComment(next)
    await client.patchBlock(boardId, comment.id, {title: text})
}

export async function deleteComment(boardId: string, comment: CommentBlock): Promise<void> {
    useComments.getState().removeComment(comment.id)
    await client.deleteBlock(boardId, comment.id)
}
